import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ActionBar,
  Avatar,
  Button,
  Card,
  Dialog,
  Disclosure,
  Icon,
  LinearProgress,
  List,
  ListItem,
  Notice,
  Skeleton,
  StageTracker,
  StateScreen,
  Tag,
  TextField,
  Timeline,
  iconCheck,
  iconShare,
} from '@sauda/ui/kit';
import { LinkButton } from '../app/LinkButton.js';
import { Screen, useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { initials } from '../lib/initials.js';
import { formatPhone } from '../lib/phone.js';
import { formatRupees } from '../lib/money.js';
import { canCall, telHref } from '../lib/next-step.js';
import { whatsappLink } from '../lib/share-text.js';
import { eventLine } from '../lib/timeline.js';
import { formatCalendarDate, formatIst } from '../lib/time.js';
import type {
  AgreementView,
  DealDetailView,
  PartnerJobView,
  RentReminderLink,
} from '../lib/types.js';
import { ClosedMoment } from './deals/ClosedMoment.js';
import { DealMenu } from './deals/DealMenu.js';
import { MemoCard } from './deals/MemoCard.js';
import { RegistrationSheet } from './deals/RegistrationSheet.js';
import { AFTER_MEMO, FINISHED, stageIndex, stagesFor } from './deals/stages.js';
import { StatusShare } from './deals/StatusShare.js';
import { PoliceCard } from './deals/PoliceCard.js';
import { TermsSheet } from './deals/TermsSheet.js';
import { ReceiptSheet } from './shared/ReceiptSheet.js';

/** One deal: who, where it stands, the one next step, and the steps already done below it. */
export function DealScreen() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [terms, setTerms] = useState(false);
  const [registration, setRegistration] = useState(false);
  const [receipt, setReceipt] = useState(false);
  const [signedMoment, setSignedMoment] = useState(false);
  const [closedAmount, setClosedAmount] = useState<number | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [rentLink, setRentLink] = useState<RentReminderLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dock, setDock] = useState<HTMLElement | null>(null);

  const deal = useQuery({
    queryKey: ['deal', id],
    queryFn: () => api<DealDetailView>(`/deals/${id}`),
    enabled: auth.status === 'authenticated',
  });
  const state = deal.data?.state ?? 'open';
  const rental = deal.data?.kind === 'rental';
  const agreement = useQuery({
    queryKey: ['agreement', id],
    queryFn: () => api<AgreementView | null>(`/deals/${id}/agreement`),
    enabled: deal.isSuccess && rental && AFTER_MEMO.includes(state),
    refetchInterval: (q) => (q.state.data != null && !q.state.data.draftReady ? 1500 : false),
  });
  const jobs = useQuery({
    queryKey: ['jobs', id],
    queryFn: () => api<PartnerJobView[]>(`/deals/${id}/partner-jobs`),
    enabled: deal.isSuccess && rental,
  });
  const memoSigned = useCallback(() => {
    setSignedMoment(true);
    void cache.invalidateQueries({ queryKey: ['deal', id] });
  }, [cache, id]);
  // The deal moves by itself when the last signature lands, so check it while signatures are out.
  useEffect(() => {
    if (state !== 'token_recorded') return;
    const timer = setInterval(() => void cache.invalidateQueries({ queryKey: ['deal', id] }), 6000);
    return () => clearInterval(timer);
  }, [state, cache, id]);

  const transition = useMutation({
    mutationFn: (v: { to: string; reason?: string }) =>
      api(`/deals/${id}/transition`, {
        method: 'POST',
        body: { to: v.to, ...(v.reason === undefined ? {} : { ownerOverrideReason: v.reason }) },
      }),
    onSuccess: async (_r, v) => {
      const received = deal.data?.brokerage.receivedPaise ?? 0;
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['deal', id] }),
        cache.invalidateQueries({ queryKey: ['deals'] }),
        cache.invalidateQueries({ queryKey: ['today'] }),
      ]);
      if (v.to === 'closed') setClosedAmount(received);
      setOverrideOpen(false);
      setError(null);
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status === 409 && e.message !== ''
          ? e.message
          : t('deal.generic'),
      ),
  });
  const rentReminder = useMutation({
    mutationFn: () =>
      api<RentReminderLink>(`/deals/${id}/rent-reminder/link`, { method: 'POST', body: {} }),
    onSuccess: setRentLink,
    onError: () => setError(t('deal.generic')),
  });

  if (deal.isPending)
    return (
      <Screen title={t('app.loading')} back="/deals">
        <Skeleton height={160} />
      </Screen>
    );
  if (deal.isError || deal.data === undefined) {
    return (
      <Screen title={t('deals.title')} back="/deals">
        <StateScreen
          kind="error"
          title={t('deals.notFound')}
          action={<Button onClick={() => void deal.refetch()}>{t('app.retry')}</Button>}
        />
      </Screen>
    );
  }
  const d = deal.data;
  const finished = FINISHED.includes(state);
  const stages = stagesFor(d.kind);
  const current = stageIndex(d.kind, state);
  const canRecord = auth.allowed('brokerage.record');
  const memoOut = d.memoSigners > 0;
  const total = d.brokerage.totalPaise;
  const percent =
    total === null || total === 0
      ? 0
      : Math.min(100, Math.round((d.brokerage.receivedPaise / total) * 100));
  const next = d.nextStep;
  const write = !chrome.readOnly;

  /** The one next step, as the docked action. Sending the memo and signing it live in the memo card. */
  const nextAction = (() => {
    if (finished || next === null || next === 'send_memo' || next === 'collect_signatures') {
      return null;
    }
    const button = (label: string, onClick: () => void, loading = false) => (
      <Button size="lg" disabled={!write} loading={loading} onClick={onClick}>
        {label}
      </Button>
    );
    if (next === 'record_token') return button(t('nextStep.record_token'), () => setTerms(true));
    if (next === 'draft_agreement') {
      return write ? (
        <LinkButton size="lg" to={`/deals/${id}/agreement`}>
          {t('nextStep.draft_agreement')}
        </LinkButton>
      ) : null;
    }
    if (next === 'register') return button(t('nextStep.register'), () => setRegistration(true));
    if (next === 'collect_brokerage') {
      return canRecord ? button(t('receipt.record'), () => setReceipt(true)) : null;
    }
    return button(
      t('nextStep.close'),
      () => transition.mutate({ to: 'closed' }),
      transition.isPending,
    );
  })();
  const nextNote = finished
    ? null
    : !write
      ? t('deal.readOnlyReason')
      : next === 'collect_brokerage' && !canRecord
        ? t('deal.accountsRecord')
        : null;
  const collecting = total !== null && (state === 'registered' || d.receipts.length > 0);
  const hero =
    total === null ? null : (
      <Card hero className={collecting ? 'k-card--strong' : undefined}>
        <p className="k-t-label-sm">{collecting ? t('deal.received') : t('deal.brokerage')}</p>
        <p className="k-t-display hero-number" data-testid="balance">
          {formatRupees(collecting ? d.brokerage.receivedPaise : total)}
        </p>
        <p className="k-t-body">
          {collecting
            ? t(d.brokerage.dueOn === null ? 'deal.receivedLineNoDue' : 'deal.receivedLine', {
                total: formatRupees(total),
                balance: formatRupees(d.brokerage.balancePaise),
                due: d.brokerage.dueOn === null ? '' : formatCalendarDate(d.brokerage.dueOn),
              })
            : t(d.brokerage.dueOn === null ? 'deal.heroLineNoDue' : 'deal.heroLine', {
                tenant: formatRupees(d.brokerage.split?.tenantPaise ?? 0),
                owner: formatRupees(d.brokerage.split?.ownerPaise ?? 0),
                due: d.brokerage.dueOn === null ? '' : formatCalendarDate(d.brokerage.dueOn),
              })}
        </p>
        {collecting ? <LinearProgress label={t('deal.brokerageProgress')} value={percent} /> : null}
      </Card>
    );

  const receiptsCard =
    total === null ? null : (
      <Card>
        <div className="card-head">
          <h2 className="k-t-title-sm">{t('deals.receipts')}</h2>
        </div>
        <List label={t('deals.receipts')}>
          {d.receipts.map((r) => (
            <ListItem
              key={r.id}
              title={formatRupees(r.amountPaise)}
              supporting={`${formatCalendarDate(r.receivedOn)} · ${t(`deals.mode_${r.mode}`)} · ${t(`deals.party_${r.payer}`)}`}
            />
          ))}
        </List>
        {canRecord && !finished && d.brokerage.balancePaise > 0 && write ? (
          <Button variant="tonal" onClick={() => setReceipt(true)}>
            {t('receipt.record')}
          </Button>
        ) : null}
      </Card>
    );

  const details = (
    <>
      {d.token.amountPaise === null && total === null ? null : (
        <Card>
          <div className="card-head">
            <h2 className="k-t-title-sm">{t('deal.termsTitle')}</h2>
            {finished || !write ? null : (
              <Button variant="text" onClick={() => setTerms(true)}>
                {t('deal.edit')}
              </Button>
            )}
          </div>
          {d.token.amountPaise === null ? null : (
            <p className="k-t-body">
              {t(d.token.mode === null ? 'deals.tokenLine' : 'deals.tokenLineMode', {
                amount: formatRupees(d.token.amountPaise),
                to: d.token.paidTo ?? '',
                mode: d.token.mode === null ? '' : t(`deals.mode_${d.token.mode}`),
                date: formatCalendarDate(d.token.date),
              })}
            </p>
          )}
          {d.owner.name === null ? null : (
            <p className="k-t-body k-muted">
              {t('deal.ownerLine', { name: d.owner.name, phone: d.owner.phone ?? '' })}
            </p>
          )}
        </Card>
      )}

      {state === 'open' ? null : <StatusShare deal={d} canEdit={write} />}
      {d.kind === 'rental' && state !== 'open' ? (
        <PoliceCard deal={d} canEdit={!finished && write} />
      ) : null}
      {next === 'send_memo' || next === 'collect_signatures' || state === 'open' ? null : (
        <MemoCard deal={d} onFullySigned={memoSigned} dock={dock} />
      )}

      {rental && AFTER_MEMO.includes(state) && agreement.data != null ? (
        <Card>
          <div className="card-head">
            <h2 className="k-t-title-sm">{t('deal.agreement')}</h2>
            {state === 'memo_signed' && write ? (
              <Link
                className="k-btn k-btn--text k-btn--md k-layer k-t-label-lg"
                to={`/deals/${id}/agreement`}
              >
                {t('deal.edit')}
              </Link>
            ) : null}
          </div>
          <div className="estimate" data-testid="estimate">
            <p className="k-t-label-sm k-muted">
              {agreement.data.estimate.label} ·{' '}
              {t('deals.rulesVersion', { version: agreement.data.estimate.rulesVersion })}
            </p>
            <p className="k-t-body">
              {t('deals.stampDuty')}: {formatRupees(agreement.data.estimate.stampDutyPaise)}
            </p>
            <p className="k-t-body">
              {t('deals.regFee')}: {formatRupees(agreement.data.estimate.registrationFeePaise)}
            </p>
            <p className="k-t-body">
              {t('deals.handling')}: {formatRupees(agreement.data.estimate.documentHandlingPaise)}
            </p>
            <p className="k-t-title-sm">
              {t('deals.estimateTotal')}: {formatRupees(agreement.data.estimate.totalPaise)}
            </p>
          </div>
          {agreement.data.draftReady && agreement.data.draftUrl !== null ? (
            <a
              className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
              href={agreement.data.draftUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('deals.openDraft')}
            </a>
          ) : agreement.data.draftReady ? null : (
            <>
              <p className="k-t-body">{t('deals.draftPreparing')}</p>
              <LinearProgress label={t('deals.draftPreparing')} />
            </>
          )}
          {agreement.data.registration === null ? null : (
            <p className="k-t-body" data-testid="registration-line">
              {t('deals.registrationLine', {
                number: agreement.data.registration.number,
                date: formatCalendarDate(agreement.data.registration.date),
              })}
            </p>
          )}
          {(jobs.data ?? []).map((j) => (
            <p key={j.id} className="k-t-label-sm k-muted">
              {j.partnerName} · {t(`deals.job_${j.status}`)}
              {j.slot === null ? '' : ` · ${j.slot}`}
            </p>
          ))}
        </Card>
      ) : null}

      {rental && ['registered', 'closed'].includes(state) ? (
        <Card>
          <h2 className="k-t-title-sm">{t('deals.rentReminder')}</h2>
          <p className="k-t-body">{t('deals.rentReminderHelp')}</p>
          <Button
            variant="tonal"
            loading={rentReminder.isPending}
            disabled={!write}
            onClick={() => rentReminder.mutate()}
          >
            {t('deals.rentReminderLink')}
          </Button>
          {rentLink === null ? null : (
            <div className="today-section">
              <label className="k-field__label k-t-label-sm" htmlFor="owner-link">
                {t('deals.rentReminderOwnerLink')}
              </label>
              <input
                id="owner-link"
                className="k-field__input"
                readOnly
                value={rentLink.link}
                onFocus={(e) => e.currentTarget.select()}
              />
              <a
                className="k-btn k-btn--filled k-btn--md k-layer k-t-label-lg"
                href={whatsappLink(
                  t('deal.rentMessage', { property: d.propertyTitle ?? '', link: rentLink.link }),
                  d.owner.phone,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon={iconShare} size={18} />
                {t('deal.shareWhatsapp')}
              </a>
              <p className="k-t-body">
                {rentLink.active
                  ? t('deals.rentReminderOn', { day: rentLink.dayOfMonth ?? 0 })
                  : t('deals.rentReminderOff')}
              </p>
            </div>
          )}
        </Card>
      ) : null}

      {collecting ? null : receiptsCard}

      <Card>
        <h2 className="k-t-title-sm">{t('deals.timeline')}</h2>
        <Timeline
          label={t('deals.timeline')}
          entries={d.events.map((ev) => ({
            key: ev.id,
            title: t(eventLine(ev.type, ev.payload).key, eventLine(ev.type, ev.payload).values),
            time: formatIst(ev.occurredAt),
          }))}
        />
      </Card>
    </>
  );

  return (
    <Screen
      title={d.propertyTitle ?? t('deals.title')}
      eyebrow={[d.kind === 'rental' ? t('deal.rentalDeal') : t('deal.saleDeal'), d.agent.name]
        .filter(Boolean)
        .join(' · ')}
      back="/deals"
      actions={<DealMenu deal={d} />}
    >
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {closedAmount !== null ? (
        <ClosedMoment
          receivedPaise={closedAmount}
          onBack={() => void navigate('/deals')}
          renewalLine={rental ? t('deal.renewalOnRadar') : null}
        />
      ) : null}
      <Card className="deal-client">
        <div className="deal-client__row">
          <Avatar initials={initials(d.client.name)} />
          <div className="row-main k-grow">
            <p className="k-t-title-sm">{d.client.name}</p>
            <p className="k-t-body k-muted k-num">
              {formatPhone(d.client.phone)} ·{' '}
              {t(d.kind === 'rental' ? 'deals.party_tenant' : 'deals.party_buyer')}
            </p>
          </div>
          {canCall(d.client.phone) ? (
            <span className="chips-row">
              <LinkButton variant="tonal" href={telHref(d.client.phone)}>
                {t('today.call')}
              </LinkButton>
              <a
                className="k-iconbtn k-iconbtn--tonal k-layer"
                href={whatsappLink('', d.client.phone)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('deal.whatsappClient')}
              >
                <Icon icon={iconShare} />
              </a>
            </span>
          ) : null}
        </div>
        {d.propertyLocation === '' ? null : (
          <p className="k-t-body k-muted">{d.propertyLocation}</p>
        )}
        <div className="deal-client__tags">
          <span data-testid="deal-state">
            <Tag>
              {state === 'token_recorded' && d.memoSigners > 0
                ? t('deal.tagMemoOut')
                : t(`deals.state_${state}`)}
            </Tag>
          </span>
          {d.hasIntroduction ? (
            <Link className="deal-client__proof" to={`/introductions/${d.introductionId ?? ''}`}>
              <Tag tone="ok">
                {d.introductionSeq === null
                  ? t('deals.viewProof')
                  : t('deal.proofRecord', { seq: d.introductionSeq })}
              </Tag>
            </Link>
          ) : (
            <Tag>{t('deals.noIntroduction')}</Tag>
          )}
          {d.renewalPrefill ? (
            <Link
              className="deal-client__proof"
              to={`/deals/${d.renewalPrefill.previousDealId}`}
              data-testid="renewal-of"
            >
              <Tag>{t('deals.renewalOf')}</Tag>
            </Link>
          ) : null}
        </div>
      </Card>
      {finished && state !== 'closed' ? null : (
        <StageTracker
          label={t('deal.stages')}
          stages={stages
            .filter((st) => st.key !== 'closed')
            .map((st) => ({ key: st.key, label: t(st.label) }))}
          current={current}
        />
      )}
      {signedMoment && next === 'draft_agreement' ? (
        <Card className="deal-moment">
          <div className="signed-moment" data-testid="memo-signed-moment">
            <span className="signed-moment__badge" aria-hidden="true">
              <Icon icon={iconCheck} size={40} />
            </span>
            <h2 className="k-t-headline">{t('deal.memoSignedTitle')}</h2>
            <Button variant="text" onClick={() => setSignedMoment(false)}>
              {t('app.close')}
            </Button>
          </div>
        </Card>
      ) : null}
      {hero}
      {collecting ? receiptsCard : null}
      {next === 'send_memo' || next === 'collect_signatures' ? (
        <MemoCard deal={d} onFullySigned={memoSigned} dock={dock} />
      ) : null}

      {collecting ? (
        <Disclosure summary={t('deal.moreDetails')}>
          <div className="k-stack">{details}</div>
        </Disclosure>
      ) : (
        details
      )}

      {nextNote === null ? null : <p className="k-t-label-sm k-muted">{nextNote}</p>}
      {nextAction === null ? null : <ActionBar label={t('deal.actions')}>{nextAction}</ActionBar>}
      <div ref={setDock} className="deal-dock" />

      <TermsSheet
        deal={d}
        open={terms}
        onClose={() => setTerms(false)}
        memoOut={memoOut && state === 'token_recorded'}
      />
      <RegistrationSheet deal={d} open={registration} onClose={() => setRegistration(false)} />
      <ReceiptSheet
        dealId={d.id}
        balancePaise={d.brokerage.balancePaise}
        title={d.propertyTitle ?? ''}
        open={receipt}
        onClose={() => setReceipt(false)}
      />
      <Dialog
        open={overrideOpen}
        onClose={() => setOverrideOpen(false)}
        title={t('deals.closeOverride')}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setOverrideOpen(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              loading={transition.isPending}
              disabled={overrideReason.trim().length < 3}
              onClick={() => transition.mutate({ to: 'closed', reason: overrideReason.trim() })}
            >
              {t('deals.closeOverride')}
            </Button>
          </>
        }
      >
        <TextField
          label={t('deals.overrideReason')}
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
        />
      </Dialog>
    </Screen>
  );
}
